import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Calendar, FileText, Layers, Loader2, Plus, Trash2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select } from '@/components/ui/Select'
import { Checkbox } from '@/components/ui/Checkbox'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { AssignmentFile, AssignmentSettings, CreateAssignmentPayload } from '@/types/assignments'

const DEFAULT_SETTINGS: AssignmentSettings = {
  allowRetries: false,
  timeSensitive: true,
  requireProctoring: false,
  deviceRestriction: 'any',
}

const assignmentSchema = z
  .object({
    courseId: z.string().min(1, 'Please select a course'),
    title: z.string().min(3, 'Title must be at least 3 characters'),
    description: z.string().min(10, 'Description should provide helpful context'),
    instructions: z.string().optional(),
    dueDate: z.string().min(1, 'Due date is required').refine((value) => {
      const date = new Date(value)
      return !Number.isNaN(date.getTime())
    }, 'Please provide a valid due date'),
    maxPoints: z.coerce.number().positive('Max points must be greater than 0'),
    settings: z.object({
      allowRetries: z.boolean(),
      timeSensitive: z.boolean(),
      requireProctoring: z.boolean(),
      deviceRestriction: z.string().min(1),
    }),
  })
  .required()

export type AssignmentFormValues = z.infer<typeof assignmentSchema>

export interface AssignmentFormInitialValues {
  courseId?: string
  title?: string
  description?: string
  instructions?: string | null
  dueDate?: string
  maxPoints?: number
  settings?: AssignmentSettings
}

export interface AssignmentFormSubmitPayload extends CreateAssignmentPayload {
  files: File[]
  filesToRemove: string[]
}

export interface CourseOption {
  id: string
  label: string
  subtitle?: string
}

interface AssignmentFormProps {
  courses: CourseOption[]
  initialValues?: AssignmentFormInitialValues
  existingAttachments?: AssignmentFile[]
  isSubmitting?: boolean
  submitLabel?: string
  onSubmit: (payload: AssignmentFormSubmitPayload) => Promise<void> | void
}

const EMPTY_ATTACHMENTS: AssignmentFile[] = []

const formatDateForInput = (iso?: string): string => {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (value: number) => String(value).padStart(2, '0')
  const year = date.getFullYear()
  const month = pad(date.getMonth() + 1)
  const day = pad(date.getDate())
  const hours = pad(date.getHours())
  const minutes = pad(date.getMinutes())
  return `${year}-${month}-${day}T${hours}:${minutes}`
}

const toIsoString = (value: string) => {
  if (!value) return value
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toISOString()
}

export function AssignmentForm({
  courses,
  initialValues,
  existingAttachments,
  isSubmitting,
  submitLabel = 'Save Assignment',
  onSubmit,
}: AssignmentFormProps) {
  const [attachments, setAttachments] = useState<File[]>([])
  const [activeAttachments, setActiveAttachments] = useState<AssignmentFile[]>([])
  const [filesToRemove, setFilesToRemove] = useState<string[]>([])

  const normalizedExistingAttachments = existingAttachments ?? EMPTY_ATTACHMENTS

  const mergedSettings = useMemo(
    () => ({
      allowRetries: initialValues?.settings?.allowRetries ?? DEFAULT_SETTINGS.allowRetries ?? false,
      timeSensitive: initialValues?.settings?.timeSensitive ?? DEFAULT_SETTINGS.timeSensitive ?? false,
      requireProctoring:
        initialValues?.settings?.requireProctoring ?? DEFAULT_SETTINGS.requireProctoring ?? false,
      deviceRestriction:
        initialValues?.settings?.deviceRestriction ?? DEFAULT_SETTINGS.deviceRestriction ?? 'any',
    }),
    [initialValues?.settings]
  )

  const defaultValues: AssignmentFormValues = useMemo(
    () => ({
      courseId: initialValues?.courseId ?? '',
      title: initialValues?.title ?? '',
      description: initialValues?.description ?? '',
      instructions: initialValues?.instructions ?? '',
      dueDate: formatDateForInput(initialValues?.dueDate) || '',
      maxPoints: initialValues?.maxPoints ?? 100,
      settings: mergedSettings,
    }),
    [initialValues, mergedSettings]
  )

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useForm<AssignmentFormValues>({
    defaultValues,
    resolver: zodResolver(assignmentSchema),
    mode: 'onBlur',
  })

  const initialValuesKeyRef = useRef<string | null>(null)
  const initialValuesKey = useMemo(() => {
    if (!initialValues) return null
    return JSON.stringify({
      courseId: initialValues.courseId ?? '',
      title: initialValues.title ?? '',
      description: initialValues.description ?? '',
      instructions: initialValues.instructions ?? '',
      dueDate: initialValues.dueDate ?? '',
      maxPoints: initialValues.maxPoints ?? 0,
      settings: mergedSettings,
    })
  }, [initialValues, mergedSettings])

  useEffect(() => {
    if (!initialValues || !initialValuesKey) {
      return
    }
    if (initialValuesKeyRef.current === initialValuesKey) {
      return
    }
    initialValuesKeyRef.current = initialValuesKey
    reset({
      courseId: initialValues.courseId ?? '',
      title: initialValues.title ?? '',
      description: initialValues.description ?? '',
      instructions: initialValues.instructions ?? '',
      dueDate: formatDateForInput(initialValues.dueDate) || '',
      maxPoints: initialValues.maxPoints ?? 100,
      settings: mergedSettings,
    })
    setAttachments([])
  }, [initialValues, initialValuesKey, mergedSettings, reset])

  useEffect(() => {
    setActiveAttachments((prev) => {
      const same =
        prev.length === normalizedExistingAttachments.length &&
        prev.every((item, index) => item.id === normalizedExistingAttachments[index]?.id)
      if (same) {
        return prev
      }
      setFilesToRemove([])
      return [...normalizedExistingAttachments]
    })
  }, [normalizedExistingAttachments])

  const selectedCourseId = watch('courseId')

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
    if (files.length) {
      setAttachments((prev) => [...prev, ...files])
      event.target.value = ''
    }
  }

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index))
  }

  const removeExistingAttachment = (id: string) => {
    setActiveAttachments((prev) => prev.filter((attachment) => attachment.id !== id))
    setFilesToRemove((prev) => (prev.includes(id) ? prev : [...prev, id]))
  }

  const handleFormSubmit = async (values: AssignmentFormValues) => {
    const payload: AssignmentFormSubmitPayload = {
      courseId: values.courseId,
      title: values.title.trim(),
      description: values.description.trim(),
      instructions: values.instructions?.trim() || undefined,
      dueDate: toIsoString(values.dueDate),
      maxPoints: values.maxPoints,
      settings: values.settings,
      files: attachments,
      filesToRemove,
    }

    await onSubmit(payload)
  }

  const selectedCourse = courses.find((course) => course.id === selectedCourseId)

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg font-semibold">
            <Layers className="h-5 w-5 text-primary" />
            Assignment Details
          </CardTitle>
          <CardDescription>
            Provide the essential information learners need to understand this assignment.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Course <span className="text-destructive">*</span>
              </label>
              <Select
                value={selectedCourseId}
                onChange={(event) => setValue('courseId', event.target.value, { shouldValidate: true })}
                icon={<BookIcon />}
              >
                <option value="" disabled>
                  Select a course
                </option>
                {courses.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.label}
                    {course.subtitle ? ` - ${course.subtitle}` : ''}
                  </option>
                ))}
              </Select>
              {selectedCourse?.subtitle && (
                <p className="text-xs text-gray-500">Department: {selectedCourse.subtitle}</p>
              )}
              {errors.courseId && <p className="text-xs text-red-600">{errors.courseId.message}</p>}
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Due Date <span className="text-destructive">*</span>
              </label>
              <Input
                type="datetime-local"
                {...register('dueDate')}
                icon={<Calendar className="h-4 w-4 text-primary" />}
              />
              {errors.dueDate && <p className="text-xs text-red-600">{errors.dueDate.message}</p>}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Assignment Title <span className="text-destructive">*</span>
              </label>
              <Input
                placeholder="e.g. Strategic Marketing Case Study"
                {...register('title')}
                icon={<FileText className="h-4 w-4 text-primary" />}
              />
              {errors.title && <p className="text-xs text-red-600">{errors.title.message}</p>}
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Maximum Points <span className="text-destructive">*</span>
              </label>
              <Input type="number" min={1} {...register('maxPoints', { valueAsNumber: true })} />
              {errors.maxPoints && <p className="text-xs text-red-600">{errors.maxPoints.message}</p>}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Description <span className="text-destructive">*</span>
            </label>
            <Textarea
              rows={4}
              placeholder="Give learners the context, deliverables, and evaluation criteria..."
              {...register('description')}
            />
            {errors.description && <p className="text-xs text-red-600">{errors.description.message}</p>}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Instructions</label>
            <Textarea
              rows={5}
              placeholder="Break the work into clear steps, attach rubrics, or embed links to supporting resources."
              {...register('instructions')}
            />
            {errors.instructions && <p className="text-xs text-red-600">{errors.instructions.message}</p>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Submission Settings</CardTitle>
          <CardDescription>Control how learners submit and access this assignment.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Controller
              control={control}
              name="settings.timeSensitive"
              render={({ field }) => (
                <PreferenceCheckbox
                  label="Time sensitive"
                  description="Learners see the countdown badge as the due date approaches."
                  checked={field.value}
                  onChange={(event) => field.onChange(event.target.checked)}
                />
              )}
            />
            <Controller
              control={control}
              name="settings.allowRetries"
              render={({ field }) => (
                <PreferenceCheckbox
                  label="Allow retries"
                  description="Learners can resubmit before the deadline."
                  checked={field.value}
                  onChange={(event) => field.onChange(event.target.checked)}
                />
              )}
            />
            <Controller
              control={control}
              name="settings.requireProctoring"
              render={({ field }) => (
                <PreferenceCheckbox
                  label="Require proctoring"
                  description="Signal that submissions should be supervised."
                  checked={field.value}
                  onChange={(event) => field.onChange(event.target.checked)}
                />
              )}
            />
            <Controller
              control={control}
              name="settings.deviceRestriction"
              render={({ field }) => (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Device restriction</label>
                  <Select value={field.value} onChange={(event) => field.onChange(event.target.value)}>
                    <option value="any">Allow any device</option>
                    <option value="desktop">Desktop only</option>
                    <option value="mobile">Mobile only</option>
                  </Select>
                  <p className="text-xs text-gray-500">
                    Choose the environment learners must use when submitting.
                  </p>
                </div>
              )}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg font-semibold">
            <PaperclipIcon />
            Attachments
          </CardTitle>
          <CardDescription>Upload supporting documents, templates, or reference materials.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3">
            <label className="text-sm font-medium text-gray-700">Upload new files</label>
            <Input
              type="file"
              multiple
              onChange={handleFileChange}
              className="cursor-pointer"
            />
            <p className="text-xs text-gray-500">You can add multiple files. PDF, DOCX, PPTX, XLSX, images, and ZIP are supported.</p>
          </div>

          {attachments.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">Pending uploads</p>
              <ul className="space-y-2">
                {attachments.map((file, index) => (
                  <li
                    key={`${file.name}-${index}`}
                    className="flex items-center justify-between rounded-md border border-dashed border-gray-300 px-4 py-2 text-sm"
                  >
                    <span className="truncate">
                      {file.name}{' '}
                      <span className="text-xs text-gray-500">({formatBytes(file.size)})</span>
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeAttachment(index)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="sr-only">Remove file</span>
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {activeAttachments.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">Existing attachments</p>
              <ul className="space-y-2">
                {activeAttachments.map((attachment) => (
                  <li
                    key={attachment.id}
                    className="flex items-center justify-between rounded-md border border-gray-200 px-4 py-2 text-sm"
                  >
                    <div className="flex flex-col">
                      <span className="font-medium">{attachment.title ?? attachment.fileName ?? 'Attachment'}</span>
                      <span className="text-xs text-gray-500">
                        {attachment.mimeType} • {formatBytes(attachment.fileSize)}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeExistingAttachment(attachment.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="sr-only">Remove attachment</span>
                    </Button>
                  </li>
                ))}
              </ul>
              {filesToRemove.length > 0 && (
                <p className="text-xs text-gray-500">
                  Removed attachments will be deleted once you save the assignment.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-3">
        <Button type="submit" disabled={isSubmitting} className="min-w-[180px]">
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Plus className="mr-2 h-4 w-4" />
              {submitLabel}
            </>
          )}
        </Button>
      </div>
    </form>
  )
}

function PreferenceCheckbox({
  label,
  description,
  checked,
  onChange,
}: {
  label: string
  description: string
  checked: boolean
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
}) {
  return (
    <div className="flex gap-3 rounded-md border border-gray-200 p-4">
      <Checkbox checked={checked} onChange={onChange} />
      <div>
        <p className="text-sm font-medium text-gray-800">{label}</p>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
    </div>
  )
}

function BookIcon() {
  return <svg className="h-4 w-4 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 4.5A2.5 2.5 0 0 1 6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
}

function PaperclipIcon() {
  return <svg className="h-5 w-5 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path
      d="M21.44 11.05 10.12 22.38a5 5 0 0 1-7.07-7.07l11.32-11.31a3 3 0 0 1 4.24 4.24L7.76 19.12"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
}

function formatBytes(bytes: number): string {
  if (!bytes || Number.isNaN(bytes)) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const value = bytes / Math.pow(1024, exponent)
  return `${value.toFixed(value >= 10 || exponent === 0 ? 0 : 1)} ${units[exponent]}`
}
